import ArrowDown16 from '@carbon/icons-react/es/arrow--down/16';
import ArrowUp16 from '@carbon/icons-react/es/arrow--up/16';
import Checkmark16 from '@carbon/icons-react/es/checkmark/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Launch16 from '@carbon/icons-react/es/launch/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import { Divider, Dropdown, Tooltip } from 'antd';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId, UserType } from 'api/models';
import { Operation } from 'api/permissions';
import { TimesheetReportsApi } from 'api/timesheets/reports/api';
import { TimesheetProjectWiseReportSummary } from 'api/timesheets/reports/types';
import { VisibilityId, VisibilityMeta } from 'api/visibility';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { InputNumber } from 'components/atoms/input';
import { format } from 'components/atoms/input-duration';
import { Loading } from 'components/atoms/loading';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { useProjectVisibilityConfig } from 'components/molecules/project-details/hooks';
import { toastService } from 'components/molecules/toast';
import {
  Progress,
  Widget,
} from 'components/organisms/projects/project-list/columns/renderer/project-hours';
import { BillabliltyChart } from 'components/organisms/timesheets/common/billability-chart';
import { CategoriesSummaryList } from 'components/organisms/timesheets/common/categories-chart';
import { formatTime } from 'components/organisms/timesheets/common/format-time';
import { getProjectSpecificTimesheetReportUrl } from 'components/organisms/timesheets/reports';
import { useCategorySummary } from 'components/organisms/timesheets/reports/use-category-summary';
import {
  asMasterList,
  useCategories,
} from 'components/pages/timesheet-settings/categories';
import { HelperText01, Text02 } from 'components/styles';
import { useProjectDetail } from 'data/projects/hooks';
import {
  useIsCurrentUserProjectAdmin,
  useReportPermission,
} from 'hooks/permissions';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateProjectHourVisibility } from 'hooks/visibility';
import { ProductiveHeading01 } from 'ozone/atoms';
import { useUpdateProjectMutation } from 'queries/project';
import { logger } from 'services';
import { defaultFormat } from 'utils/date-field/formatter';
import { getProjectFinancialsSettingsUrl } from 'utils/urls';
import { ResponsiveCard } from '../../style';
import { Half, Row } from './components';

const StyledMenu = styled(Menu)`
  width: 256px;
`;

const StyledMenuItem = styled(Menu.Item)`
  white-space: normal;
  align-items: flex-start !important;
  gap: var(--spacing-04);

  span {
    position: relative;
    bottom: var(--spacing-02);
  }
`;

const Card = styled(ResponsiveCard)`
  min-height: 200px;
  max-height: unset;
  transition: height 0.5s linear;
  &:before {
    padding-bottom: 0%;
  }

  .ant-card-head {
    position: unset;
  }

  .ant-card-body {
    position: unset;
  }
`;

const RowStyled = styled.div<{ isPrivate: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 54px;
    z-index: 0;
    background: ${({ isPrivate }) => isPrivate && 'var(--private-01)'};
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;
const Header = styled.div``;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
`;
const SubTitle = styled.p`
  ${HelperText01};
  ${Text02};
`;
const CategoryHeaderRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const CategorySummary = styled(CategoriesSummaryList)`
  width: 100%;
`;

const BudgetHoursTextWrapper = styled.span`
  display: flex;
  align-items: center;
`;
const LeftAlignedButton = styled(Button)`
  padding-left: 0;
`;
const BudgetInput = styled.div`
  display: flex;
  align-items: center;

  .ant-input-number {
    border: 1px solid var(--border-color);
    background: transparent;
    border-radius: 4px;
    height: 30px;
    width: 120px;

    .ant-input-number-handler-wrap {
      display: none;
    }
    .ant-input-number-input-wrap input {
      height: 30px;
    }
  }
`;

const RedButton = styled(Button)`
  color: var(--red-50);
`;

const GreenButton = styled(Button)`
  color: var(--green-50);
`;

const EditBudgetHours = styled(Edit16)`
  visibility: hidden;
  &:hover {
    color: var(--blue-60);
  }
`;

const EditHoursWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  &:hover {
    ${EditBudgetHours} {
      visibility: visible;
    }
  }
`;

const BugdetHourInput: FC<{
  projectId: number;
  editable: boolean;
  isFinancialsEnabled: boolean;
  bugdetHours?: number;
}> = ({ projectId, bugdetHours, editable, isFinancialsEnabled }) => {
  const { mutate, isLoading: isSaving } = useUpdateProjectMutation(
    ProjectId(projectId),
  );
  const [inputVisible, setInputVisible] = useState(false);
  const [_budgetHours, _setBudgetHours] = useState(bugdetHours);

  const errorMessage = useTranslation('error');
  const errorTitle = useTranslation('error_saving');
  const inputPlaceholder = useTranslation(
    'templates.budget_hours.placeholder' as TranslationKey,
  );

  const onBudgetFieldSave = (value) => {
    mutate(
      { budgetedHours: value },
      {
        onSuccess: () => {
          setInputVisible(false);
        },
        onError: () => {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
          setInputVisible(false);
        },
      },
    );
  };

  const save = () => {
    onBudgetFieldSave(_budgetHours);
  };
  const redirectToFinancials = () => {
    window.open(
      getProjectFinancialsSettingsUrl(ProjectId(projectId)),
      '_blank',
    );
  };

  return editable && !isFinancialsEnabled ? (
    <>
      {inputVisible ? (
        <BudgetInput>
          <InputNumber
            value={_budgetHours}
            placeholder={inputPlaceholder}
            precision={0}
            min={1}
            type="number"
            onPressEnter={save}
            onChange={(e) => {
              _setBudgetHours(e as number);
            }}
            data-cy="set-budget-hours-input"
          />
          <GreenButton
            type="text"
            size="small"
            loading={isSaving}
            disabled={!_budgetHours}
            icon={<Checkmark16 />}
            onClick={save}
            data-cy="set-budget-hours-accept"
          />
          <RedButton
            type="text"
            size="small"
            disabled={isSaving}
            icon={<Close16 />}
            onClick={() => setInputVisible(false)}
            data-cy="set-budget-hours-decline"
          />
        </BudgetInput>
      ) : (
        <>
          {bugdetHours == null ? (
            <LeftAlignedButton
              type="link"
              onClick={() => {
                _setBudgetHours(bugdetHours);
                setInputVisible(true);
              }}
              data-cy="set-budget-hours-cta"
            >
              <FormattedMessage id="templates.set_bugdeted_hours_button" />
            </LeftAlignedButton>
          ) : (
            <BudgetHoursTextWrapper>
              <span data-test-id="budgeted-hours">
                {format('hh', bugdetHours)}
              </span>
              <Button
                type="text"
                icon={<Edit16 />}
                size="small"
                onClick={() => {
                  _setBudgetHours(bugdetHours);
                  setInputVisible(true);
                }}
              />
            </BudgetHoursTextWrapper>
          )}
        </>
      )}
    </>
  ) : isFinancialsEnabled ? (
    <EditHoursWrapper>
      {bugdetHours == null ? '-' : format('hh', bugdetHours)}

      <EditBudgetHours onClick={redirectToFinancials} />
    </EditHoursWrapper>
  ) : (
    <>{bugdetHours == null ? '-' : format('hh', bugdetHours)}</>
  );
};

export const ProjectTimeInfo: FC<{
  projectId: number;
}> = ({ projectId }) => {
  const { user } = useUser();
  const [projectReport, setReport] =
    useState<TimesheetProjectWiseReportSummary>();

  const { data: project } = useProjectDetail(ProjectId(projectId));

  const isBudgetHoursEditable = useIsCurrentUserProjectAdmin(
    ProjectId(projectId),
  );
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);

  const config = useProjectVisibilityConfig(['project_id', projectId]);
  const updateProjectConfig = useUpdateProjectHourVisibility(projectId);

  const budget_hours = project?.budgetedHours;
  const startDate =
    project?.startDate ??
    defaultFormat(moment().subtract(90, 'day').startOf('day'));
  const endDate = defaultFormat(moment().endOf('week'));
  const hasReportReadPermission = useReportPermission().includes(
    Operation.Read,
  );
  const { categories: _categories } = useCategories();
  const timeEntryCategories = useMemo(
    () => asMasterList(_categories).map(([_, x]) => x),
    [_categories],
  );

  const { categoriesSummary } = useCategorySummary(
    projectReport?.categories ?? [],
    timeEntryCategories,
  );

  const [isWidgetLoading, setIsWidgetLoading] = useState(true);

  useEffect(() => {
    TimesheetReportsApi.getProjectWiseSummary(projectId)
      .then((report) => {
        setIsWidgetLoading(false);
        setReport(report);
      })
      .catch((error) => {
        setIsWidgetLoading(false);
        logger.error('error in loading time info widget', { error });
      });
  }, [projectId, setReport]);

  const total_minutes = projectReport
    ? user.userType === UserType.Native
      ? projectReport?.billableMinutes + projectReport?.nonBillableMinutes
      : projectReport?.billableMinutes
    : 0;
  const budgeted_minutes = (budget_hours ?? 0) * 60;
  const remaining_minutes = budgeted_minutes - total_minutes;
  const showMoreAvailable = categoriesSummary.length > 3;

  const [showAll, setShowAll] = useState(false);

  const projectHoursToggle = useMemo(
    () => config?.visibilityMeta?.projectHoursToggle ?? false,
    [config?.visibilityMeta?.projectHoursToggle],
  );

  const handleToggleForCutomer = useCallback(() => {
    if (config?.visibilityId) {
      updateProjectConfig(
        { projectHoursToggle: !projectHoursToggle } as VisibilityMeta,
        VisibilityId(config.visibilityId),
      );
    }
  }, [config?.visibilityId, projectHoursToggle, updateProjectConfig]);

  const MoreActionsMenu = () => (
    <StyledMenu>
      <StyledMenuItem
        icon={projectHoursToggle ? <ViewOff16 /> : <View16 />}
        onClick={handleToggleForCutomer}
      >
        {projectHoursToggle ? (
          <FormattedMessage id="project.overview.hide.billable.hours" />
        ) : (
          <FormattedMessage id="project.overview.show.billable.hours" />
        )}
      </StyledMenuItem>
    </StyledMenu>
  );

  return (
    <Card
      title={
        <RowStyled isPrivate={!projectHoursToggle}>
          <Header>
            <strong>
              <FormattedMessage id="project.overview.project_time_details.title" />
            </strong>
            {!projectHoursToggle && (
              <SubTitle>
                <FormattedMessage
                  id="project.overview.project_details.sub_title"
                  values={{ companyName: user.company.companyName }}
                />
              </SubTitle>
            )}
          </Header>
          <HideForCustomer>
            <Dropdown
              overlay={MoreActionsMenu}
              trigger={['click']}
              placement="bottomRight"
            >
              <Tooltip title={<FormattedMessage id="more_actions" />}>
                <Button
                  type="text"
                  icon={<OverflowMenuVertical16 />}
                  size="small"
                  data-cy="project-hours-more-actions"
                />
              </Tooltip>
            </Dropdown>
          </HideForCustomer>
        </RowStyled>
      }
    >
      {isWidgetLoading ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        <>
          {budget_hours == null ? null : (
            <Row>
              <Progress
                total_minutes={total_minutes}
                width="100%"
                height={24}
                budget_hours={budget_hours}
                hasMarker
              />
            </Row>
          )}
          <Row>
            <Half>
              <Widget
                title={<FormattedMessage id="projects.list.tracked_hours" />}
                value={total_minutes === 0 ? '-' : formatTime(total_minutes)}
                percentage={
                  budget_hours == null || total_minutes === 0
                    ? undefined
                    : Math.round((total_minutes * 100) / budgeted_minutes)
                }
              />
            </Half>
            <Half>
              <Widget
                title={<FormattedMessage id="projects.list.remaining_hours" />}
                value={
                  budget_hours == null ? '-' : formatTime(remaining_minutes)
                }
                percentage={
                  budget_hours == null
                    ? null
                    : Math.round((remaining_minutes * 100) / budgeted_minutes)
                }
              />
            </Half>
          </Row>
          <Row>
            <Half>
              <Widget
                title={<FormattedMessage id="projects.list.budgeted_hours" />}
                value={
                  <BugdetHourInput
                    bugdetHours={budget_hours}
                    projectId={projectId}
                    isFinancialsEnabled={isFinancialsEnabled}
                    editable={isBudgetHoursEditable}
                  />
                }
              />
            </Half>
          </Row>
          <HideForCustomer>
            {total_minutes === 0 ? null : (
              <>
                <Divider />
                <BillabliltyChart
                  billableMinutes={projectReport?.billableMinutes ?? 0}
                  nonBillableMinutes={projectReport?.nonBillableMinutes ?? 0}
                />
                <Divider />
                <CategoryHeaderRow>
                  <ProductiveHeading01>
                    <FormattedMessage id="timesheets.summary.categories" />
                  </ProductiveHeading01>
                  {hasReportReadPermission ? (
                    <Link
                      to={getProjectSpecificTimesheetReportUrl(
                        projectId,
                        startDate,
                        endDate,
                      )}
                      target="_blank"
                    >
                      <Button type="link" size="small" icon={<Launch16 />}>
                        <FormattedMessage id="view_detail" />
                      </Button>
                    </Link>
                  ) : null}
                </CategoryHeaderRow>
                <Row>
                  <CategorySummary
                    categories={categoriesSummary.slice(
                      0,
                      showAll ? categoriesSummary.length : 3,
                    )}
                  />
                </Row>
                {showMoreAvailable ? (
                  <Row>
                    {showAll ? (
                      <LeftAlignedButton
                        type="link"
                        icon={<ArrowUp16 />}
                        size="small"
                        iconPlacement="right"
                        onClick={() => setShowAll(false)}
                      >
                        <FormattedMessage id="show_less" />
                      </LeftAlignedButton>
                    ) : (
                      <LeftAlignedButton
                        type="link"
                        icon={<ArrowDown16 />}
                        size="small"
                        iconPlacement="right"
                        onClick={() => setShowAll(true)}
                      >
                        <FormattedMessage id="show_more" />
                      </LeftAlignedButton>
                    )}
                  </Row>
                ) : null}
              </>
            )}
          </HideForCustomer>
        </>
      )}
    </Card>
  );
};
